import React, {useEffect, useState, useRef} from "react";
import PropTypes from "prop-types";
import { formatAttributeFromApi, formatPhoneNumber, cleanPhoneNumber } from '../utils/utils';
import PivotalFormApi from "../../../js/csf/pivotal/PivotalFormApi";
import {startLoadingSpinner, stopLoadingSpinner} from "../../csf-ufc-pivotal/includes/utils";

export default function CsfMembreProfileModal({apiUrl, apiEnv, accessToken, member, getConnectedUser}) {
  const [isError, setIsError] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const dismissButton = useRef();

  useEffect(() => {
    const phoneNumber = member?.Contact_Phone__Rn_Descriptor;
    const email = member?.Contact_Email__Rn_Descriptor;
    if(phoneNumber){
      setPhone(formatPhoneNumber(phoneNumber) || "");
    }
    if(email){
      setEmail(formatAttributeFromApi(email) || "");
    }
  }, [member]);

  const postMemberToApi = (event) => {
    event.preventDefault();
    startLoadingSpinner();

    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);

    const requestData = {
      form: 'CSF_EM_Contact_Rest',
      RecordId: member?.Contact_Id,
      deltaData: {
        Phone: cleanPhoneNumber(phone.trim()),
        Email: formatAttributeFromApi(email.trim()),
      }
    }

    pivotalFormApi.callFormPutEndpoint(requestData, '_Espace_Membre')
      .then(response => {
        if (response?.success && Object.keys(response?.payload?.data?.primary).length > 0) {
          /*
            On refait un appel a l'api pour aller chercher le membre connecté et resetter son state
            pour palier au fait que les noms des attributs du membre qui nous sont retournés dans la réponse du PUT
            ne sont pas les mêmes que dans le fetch d'un membre. On s'assure ainsi du côté applicatif qu'un membre
            provenant de l'api ait toujours les mêmes noms d'attributs
          */
          getConnectedUser();
        }
        setIsError(false);
        stopLoadingSpinner();
        dismissButton.current.click();
      })
      .catch(error => {
        setIsError(true);
        console.error(error);
        stopLoadingSpinner();
      });
  }

  const changePhone = (event) => {
    setPhone(event.target.value);
  }

  const changeEmail = (event) => {
    setEmail(event.target.value);
  }

  return (
    <div className="modal fade" id="membreProfilModal" tabIndex="-1" role="dialog" aria-labelledby="membreProfilModalTitle"
      aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <form className="modal-content" onSubmit={postMemberToApi}>
          <div className="modal-header d-flex align-items-center">
            <h2 id="membreProfilModalTitle">{window.Drupal.t('membre.profil.modal.title')}</h2>
            <button type="button" className="close" data-dismiss="modal" aria-label={window.Drupal.t('lang.modal.close')}>
              <i className="icon icon-csf-close-noborder"><span className="d-none">&nbsp;</span></i>
            </button>
          </div>

          <div className="modal-body">
            {isError === true && (
              <div className="alert alert-danger mt-1" role="alert"
                aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
            )}
            <fieldset>
              <legend
                className="sr-only">{window.Drupal.t('fournisseur.gestion.table.firstname')} {window.Drupal.t('fournisseur.gestion.table.lastname')}</legend>
              <div className="row gutter-small">
                <div className="col-12 col-xl-6">
                  <div className="form-group">
                    <label htmlFor="First_Name">{window.Drupal.t('fournisseur.gestion.table.firstname')}*</label>
                    <input value={member?.Prénom || ""}
                      type="text" id="First_Name" name="First_Name"
                      disabled />
                  </div>
                </div>
                <div className="col-12 col-xl-6">
                  <div className="form-group">
                    <label htmlFor="Last_Name">{window.Drupal.t('fournisseur.gestion.table.lastname')}*</label>
                    <input value={member?.Nom || ""}
                      type="text" id="Last_Name" name="Last_Name"
                      disabled />
                  </div>
                </div>
              </div>
            </fieldset>

            <div className="row gutter-small">
              <div className="col-12 col-xl-6">
                <div className="form-group">
                  <label
                    htmlFor="certificate_number">{window.Drupal.t('membre.profil.modal.label-certificate')}</label>
                  <input value={member?.No_Dossier || ""}
                    type="text" id="certificate_number" name="certificate_number" disabled />
                </div>
              </div>
              <div className="col-12 col-xl-6">
                <div className="form-group">
                  <label
                    htmlFor="member_phone">{window.Drupal.t('Telephone')}</label>
                  <input
                    onChange={changePhone}
                    value={phone || ""}
                    type="text"
                    id="member_phone"
                    name="phone" />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="Contact_Email">{window.Drupal.t('fournisseur.gestion.table.email')}</label>
                  <input
                    onChange={changeEmail}
                    value={formatAttributeFromApi(email) || ""}
                    type="text"
                    id="Contact_Email"
                    name="email" />
                </div>
              </div>
            </div>

            <div className="membre-modal-box p-1">
              <h3 className="mb-1">{window.Drupal.t('membre.profil.modal.address.title')}</h3>
              <div dangerouslySetInnerHTML={{__html: window.Drupal.t('membre.profil.modal.address.content')}}></div>
            </div>
          </div>

          <div className="modal-footer">
              <div className="space-between-wrapper">
                <button type="button" className='button btn-outlined btn-larger' data-dismiss="modal" ref={dismissButton}>{window.Drupal.t('fournisseur.back.btn')}</button>
                <input className="btn-submit btn-pale btn-larger" type="submit" value={window.Drupal.t('fournisseur.gestion.submit.label')}/>
              </div>
            </div>
        </form>
      </div>
    </div>
  )
}

CsfMembreProfileModal.propTypes = {
  apiUrl: PropTypes.string.isRequired,
  apiEnv: PropTypes.string.isRequired,
  accessToken: PropTypes.string.isRequired,
  member: PropTypes.object.isRequired,
  getConnectedUser: PropTypes.func.isRequired,
};

